import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import JsonLd from "@/components/site/JsonLd";
import { Reveal } from "@/components/site/Reveal";
import { graph, organizationNode, aboutPageNode, breadcrumbNode } from "@/lib/schema";
import { useSiteInfo } from "@/lib/siteInfo";
import { useSeo } from "@/lib/seo";
import { useLang } from "@/lib/i18n";
import { LT } from "@/lib/localize";
import api, { resolveMediaUrl } from "@/lib/api";
import { imgSrc, imgSet } from "@/lib/img";

const PAGE_KEYS = ["title", "intro", "seo_title", "seo_description"];

export default function About() {
  const { t, lang } = useLang();
  const [raw, setRaw] = useState(null);
  const siteInfo = useSiteInfo();

  useEffect(() => {
    api.get("/pages/chi-siamo").then((r) => setRaw(r.data)).catch(() => {});
  }, []);

  const page = raw ? LT(raw, lang, PAGE_KEYS) : null;
  const sections = raw ? raw.sections.map((s) => LT(s, lang, ["title", "text"])) : [];
  const cover = resolveMediaUrl(raw && raw.cover);
  const title = (page && page.seo_title) || t("seo.about.title");
  const description = (page && page.seo_description) || t("seo.about.desc");

  useSeo({ title, description, path: "/chi-siamo", type: "article", image: cover || undefined });

  const jsonLd = graph([
    organizationNode(siteInfo || {}),
    aboutPageNode({ title: (page && page.title) || t("nav.about"), description, lang }),
    breadcrumbNode([{ name: "Home", path: "/" }, { name: t("nav.about"), path: "/chi-siamo" }], lang),
  ]);

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd id="jsonld-about" data={jsonLd} />

      <section className="pt-32 sm:pt-44 pb-16" data-testid="about-page">
        <div className="container-x">
          <Reveal className="max-w-3xl">
            <p className="eyebrow mb-3">{t("about.eyebrow")}</p>
            <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[0.98]" data-testid="about-title">
              {(page && page.title) || t("nav.about")}
            </h1>
            {page && page.intro && (
              <p className="mt-7 text-base sm:text-lg text-cream2 leading-relaxed" data-testid="about-intro">{page.intro}</p>
            )}
          </Reveal>
        </div>
      </section>

      {cover && (
        <section className="pb-4">
          <div className="container-x">
            <Reveal>
              <img
                src={imgSrc(cover, 1600)}
                srcSet={imgSet(cover, [800, 1200, 1600])}
                sizes="100vw"
                alt={(page && page.title) || "De Los Cielos"}
                className="w-full aspect-[21/9] object-cover border border-white/5"
                loading="lazy"
                decoding="async"
                data-testid="about-cover"
              />
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-24" data-testid="about-sections">
        <div className="container-x max-w-5xl">
          {sections.map((s, i) => (
            <Reveal key={s.id} delay={0.04} className="border-t border-white/8 py-10 sm:py-14 grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-4">
                <p className="font-mono text-[11px] text-copper tracking-[0.25em]">0{i + 1}</p>
                <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-tight" data-testid={`about-section-title-${i + 1}`}>{s.title}</h2>
              </div>
              <div className="md:col-span-8">
                <p className="text-sm sm:text-base text-cream2 leading-relaxed whitespace-pre-line">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="container-x max-w-5xl grid grid-cols-1 sm:grid-cols-3 gap-px bg-copper/20 border border-copper/20">
          {[
            { to: "/viaggi", label: t("about.ctaTours"), id: "about-cta-tours" },
            { to: "/leader", label: t("about.ctaLeaders"), id: "about-cta-leaders" },
            { to: "/faq", label: t("about.ctaFaq"), id: "about-cta-faq" },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={c.id}
              className="bg-obsidian p-7 group flex items-center justify-between gap-4 hover:bg-obsidian2 transition-colors"
            >
              <span className="font-serif text-xl">{c.label}</span>
              <ArrowRight className="h-5 w-5 text-copper transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
